import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock3, History, RotateCcw, Save, Trophy } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


const name = (fighter) => fighter ? `${fighter.first_name} ${fighter.last_name}` : "Freilos";

const resultLabel = (type) => ({
  points: "Punktsieg", ko: "KO", tko: "TKO", injury: "Verletzt", dq: "DQ", walkover: "Walkover", bye: "Freilos", draw: "Unentschieden", sparring: "Sparringskampf",
}[type] || (type || "").toUpperCase());

const NO_WINNER_TYPES = new Set(["draw", "sparring"]);

export default function MatchPageV2() {
  const { id } = useParams();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [fighterLookup, setFighterLookup] = useState({});
  const [form, setForm] = useState({ winner_id: "", result_type: "points", ring_number: "", scheduled_time: "" });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [byeCandidates, setByeCandidates] = useState([]);
  const [selectedByeFighter, setSelectedByeFighter] = useState("");
  const admin = ["super_admin", "event_admin"].includes(user?.role);

  const load = useCallback(async () => {
    const response = await api.get(`/matches/${id}`);
    setMatch(response.data);
    setForm((current) => ({
      winner_id: response.data.winner_id || response.data.fighter1_id || response.data.fighter2_id || "",
      result_type: response.data.result_type || "points",
      ring_number: response.data.ring_number || "",
      scheduled_time: response.data.scheduled_time || "",
    }));
    try {
      const hist = await api.get(`/matches/${id}/history`);
      setHistory(hist.data);
      const lookup = {};
      if (response.data.fighter1) lookup[response.data.fighter1.id] = response.data.fighter1;
      if (response.data.fighter2) lookup[response.data.fighter2.id] = response.data.fighter2;
      setFighterLookup(lookup);
    } catch (_e) {
      setHistory([]);
    }
    // Bye-Kandidaten: alle Kämpfer der Kategorie laden, um Freiloskampf besetzen zu können
    if (response.data.result_type === "bye" && response.data.event_id) {
      try {
        const [cats, allFighters] = await Promise.all([
          api.get(`/events/${response.data.event_id}/categories`),
          api.get(`/events/${response.data.event_id}/fighters`),
        ]);
        const cat = cats.data.find((c) => c.id === response.data.category_id);
        if (cat) {
          const inThisMatch = new Set([response.data.fighter1_id, response.data.fighter2_id]);
          const candidates = allFighters.data.filter((f) =>
            f.weight_class === cat.weight_class && f.gender === cat.gender && !inThisMatch.has(f.id)
          );
          setByeCandidates(candidates);
        }
      } catch (_e) { setByeCandidates([]); }
    }
  }, [id]);

  useEffect(() => {
    load().catch((requestError) => setError(apiError(requestError)));
  }, [load]);

  const save = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const noWinner = NO_WINNER_TYPES.has(form.result_type);
    // Warnung bei Bracket-Match mit Folgekampf → Slot wird leer.
    if (noWinner && match?.next_match_id) {
      const label = form.result_type === "draw" ? "Unentschieden" : "Sparringskampf";
      const ok = window.confirm(
        `${label} setzen?\n\nDieser Kampf hat einen Folgekampf im Turnierbaum. Ohne Sieger bleibt der Folge-Slot LEER. Du kannst ihn danach manuell füllen oder den Folgekampf löschen.\n\nFortfahren?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const response = await api.put(`/matches/${id}/result`, {
        winner_id: noWinner ? null : form.winner_id,
        result_type: form.result_type,
        ring_number: form.ring_number ? Number(form.ring_number) : null,
        scheduled_time: form.scheduled_time || null,
      });
      setMatch({ ...match, ...response.data });
      setNotice(match?.status === "completed"
        ? "Ergebnis korrigiert. Folgerunde wurde automatisch angepasst."
        : (noWinner ? "Ergebnis gespeichert. Der Folgekampf bleibt ohne Sieger — bitte manuell füllen oder löschen." : "Ergebnis bestätigt. Sieger wurde in die nächste Runde übernommen."));
      await load();
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await api.delete(`/matches/${id}/result`);
      setNotice("Ergebnis zurückgenommen. Kampf steht wieder auf offen; abhängige Folgerunden wurden zurückgesetzt.");
      await load();
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const fillBye = async () => {
    if (!selectedByeFighter) { setError("Bitte einen Kämpfer auswählen."); return; }
    setError(""); setNotice(""); setBusy(true);
    try {
      const r = await api.post(`/matches/${id}/fill-bye`, { fighter_id: selectedByeFighter });
      setNotice(r.data.message + (r.data.warnings?.length ? " ⚠ " + r.data.warnings.join(" ") : ""));
      setSelectedByeFighter("");
      await load();
    } catch (requestError) {
      setError(apiError(requestError));
    } finally { setBusy(false); }
  };

  if (error && !match) return <div className="content-frame"><div className="form-error" data-testid="match-detail-error">{error}</div></div>;
  if (!match) return <div className="content-frame loading" data-testid="match-detail-loading">Kampf wird geladen …</div>;

  const isBye = match.result_type === "bye";
  const bothFightersSet = !!match.fighter1_id && !!match.fighter2_id;
  const canEdit = admin && bothFightersSet && !isBye;
  const isCompleted = match.status === "completed";

  const fighterName = (fid) => {
    if (!fid) return "—";
    const f = fighterLookup[fid];
    return f ? `${f.first_name} ${f.last_name}` : fid.slice(0, 8);
  };

  return (
    <div className="content-frame narrow" data-testid="match-detail-page">
      <Link to={`/events/${match.event_id}`} className="back-link" data-testid="back-to-event-from-match-link">← ZUM EVENT</Link>
      <p className="eyebrow">RUNDE {match.round} · KAMPF {match.position + 1}</p>
      <h1>KAMPFDETAIL</h1>
      <section className="versus-card">
        <div data-testid="match-fighter-one">
          <span>BLAU</span>
          <h2>{name(match.fighter1)}</h2>
          <small>{match.fighter1?.club}</small>
          {isCompleted && match.result_type === "draw" && <b style={{color:"#f4d33e"}} data-testid="match-blue-draw-badge">UNENTSCHIEDEN</b>}
          {isCompleted && match.result_type === "sparring" && <b style={{color:"#9aa0b0"}} data-testid="match-blue-sparring-badge">SPARRING</b>}
          {isCompleted && match.winner_id === match.fighter1_id && <b style={{color:"#2fb865"}}>✓ SIEGER</b>}
        </div>
        <b>VS</b>
        <div data-testid="match-fighter-two">
          <span>ROT</span>
          <h2>{name(match.fighter2)}</h2>
          <small>{match.fighter2?.club}</small>
          {isCompleted && match.result_type === "draw" && <b style={{color:"#f4d33e"}} data-testid="match-red-draw-badge">UNENTSCHIEDEN</b>}
          {isCompleted && match.result_type === "sparring" && <b style={{color:"#9aa0b0"}} data-testid="match-red-sparring-badge">SPARRING</b>}
          {isCompleted && match.winner_id === match.fighter2_id && <b style={{color:"#2fb865"}}>✓ SIEGER</b>}
        </div>
      </section>
      <div className="match-meta">
        <span><Clock3 /> Ring {match.ring_number || "offen"}</span>
        <span><Trophy /> {isCompleted ? `Ergebnis: ${resultLabel(match.result_type)}` : "Ausstehend"}</span>
      </div>
      {notice && <div className="success-notice" data-testid="match-result-notice"><CheckCircle2 size={18} />{notice}</div>}
      {error && <div className="form-error" data-testid="match-result-error">{error}</div>}
      {canEdit && (
        <form className="create-event-form match-result-form" onSubmit={save} data-testid="match-result-form">
          <b>{isCompleted ? "ERGEBNIS KORRIGIEREN" : "OFFIZIELLES ERGEBNIS"}</b>
          {isCompleted && <p className="muted" style={{color:"#ffd580"}}>Kampf ist bereits abgeschlossen. Änderung überschreibt das bisherige Ergebnis und passt die Folgerunde automatisch an.</p>}
          <div className="form-grid">
            {!NO_WINNER_TYPES.has(form.result_type) && <label>Sieger
              <select value={form.winner_id} onChange={(event) => setForm({ ...form, winner_id: event.target.value })} data-testid="match-winner-select">
                <option value={match.fighter1_id}>{name(match.fighter1)} · BLAU</option>
                <option value={match.fighter2_id}>{name(match.fighter2)} · ROT</option>
              </select>
            </label>}
            <label>Entscheidung
              <select value={form.result_type} onChange={(event) => setForm({ ...form, result_type: event.target.value })} data-testid="match-result-type-select">
                <option value="points">DECISION · PUNKTSIEG</option>
                <option value="ko">KO · KNOCKOUT</option>
                <option value="tko">TKO · TECHNISCHER KO</option>
                <option value="injury">VERLETZT</option>
                <option value="dq">DQ · DISQUALIFIKATION</option>
                <option value="walkover">WALKOVER</option>
                <option value="draw">UNENTSCHIEDEN · KEIN SIEGER</option>
                <option value="sparring">SPARRINGSKAMPF · KEIN SIEGER, KEINE BILANZ</option>
              </select>
            </label>
            <label>Ring
              <input type="number" min="1" max="12" value={form.ring_number} onChange={(event) => setForm({ ...form, ring_number: event.target.value })} data-testid="match-ring-input" />
            </label>
            <label>Zeit
              <input type="datetime-local" value={form.scheduled_time} onChange={(event) => setForm({ ...form, scheduled_time: event.target.value })} data-testid="match-time-input" />
            </label>
          </div>
          <div className="form-actions">
            {isCompleted && (
              <button type="button" className="button ghost danger-button" onClick={withdraw} disabled={busy} data-testid="withdraw-result-button">
                <RotateCcw size={17} /> ERGEBNIS ZURÜCKNEHMEN
              </button>
            )}
            <button className="button primary" disabled={busy} data-testid="save-match-result-button">
              <Save size={18} /> {isCompleted ? "KORREKTUR SPEICHERN" : "ERGEBNIS BESTÄTIGEN"}
            </button>
          </div>
        </form>
      )}
      {admin && isBye && (
        <div className="create-event-form match-result-form" data-testid="fill-bye-form">
          <b>FREILOS-PLATZ BESETZEN</b>
          <p className="muted">{match.fighter1 ? `${name(match.fighter1)} (BLAU)` : "Blau-Ecke leer"} bleibt unverändert. Wähle den Kämpfer für die rote Ecke aus der Kategorie.</p>
          <div className="form-grid">
            <label>Neuer Gegner (ROT)
              <select value={selectedByeFighter} onChange={(e) => setSelectedByeFighter(e.target.value)} data-testid="fill-bye-select">
                <option value="">— Auswählen —</option>
                {byeCandidates.map((f) => (
                  <option key={f.id} value={f.id}>{f.first_name} {f.last_name} · {f.club}</option>
                ))}
              </select>
            </label>
          </div>
          {byeCandidates.length === 0 && <p className="muted">Keine passenden Kämpfer in dieser Kategorie gefunden. Der Kämpfer muss dieselbe Gewichtsklasse und dasselbe Geschlecht haben wie diese Kategorie.</p>}
          <div className="form-actions">
            <button className="button primary" onClick={fillBye} disabled={busy || !selectedByeFighter} data-testid="fill-bye-button">Freilos besetzen</button>
          </div>
        </div>
      )}
      {admin && !bothFightersSet && !isBye && (
        <div className="stale-banner" data-testid="match-awaiting-fighter-banner">
          Dieser Kampf wartet noch auf einen Gegner.
        </div>
      )}
      {history.length > 0 && (
        <section className="match-history" data-testid="match-history">
          <p className="eyebrow"><History size={13} /> HISTORIE</p>
          <ul>
            {history.map((entry) => (
              <li key={entry.id} data-testid={`history-entry-${entry.id}`}>
                <b>{new Date(entry.changed_at).toLocaleString("de-DE")}</b>
                <span>{entry.action === "result_entered" ? "Ergebnis eingetragen" : entry.action === "correction" ? "Ergebnis korrigiert" : "Ergebnis zurückgenommen"}</span>
                <small>
                  {entry.old_winner_id && `War: ${fighterName(entry.old_winner_id)} (${resultLabel(entry.old_result_type)}) → `}
                  {entry.new_winner_id ? `${fighterName(entry.new_winner_id)} (${resultLabel(entry.new_result_type)})` : "offen"}
                </small>
                <small>durch {entry.changed_by_email || entry.changed_by}</small>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
